import { Injectable } from "@angular/core";
import { PRIMITIVE, ServiceMessageBrokerFactory } from "shared/provider-webworker";

import { ClientLookupService } from "./ClientLookupService";
import { IClientPromiseLookupService } from "../IClientPromiseLookupService";
import { AutoCompleteLabelValuePrimaryLookupService } from "./AutoCompleteLabelValuePrimaryLookupService";

import { BranchLookupService } from "../../../lookup/model/impl/BranchLookupService";
import { BranchPromiseLookupService } from "../../../lookup/model/impl/BranchPromiseLookupService";
import { ClientModelPrivatization } from "../../../model/privatization/ClientModelPrivatization";

import { CubiqWorker } from "../../../worker/CubiqWorker";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { ClientContactImpl } from "shared/cubiq-model-multi-asset";
import { SystemConfigPrimaryService } from "shared/auth-services";
import { ActiveUserProfilePromiseService } from "shared/auth-services";
import { ActiveUser } from "../../../model/core/ActiveUser";
import { ClientAccountVisibilityService } from "../../../model/privatization/clientAccountVisibility/ClientAccountVisibilityService";
import { ClientAccountVisibilityType } from "../../../model/privatization/clientAccountVisibility/ClientAccountVisibilityType";

const TRADING_CHANNEL_PROFILE_VALUE: CubiqTS.ConcreteEncodable = CubiqTS.ENCODABLE(
  CubiqTS.TradingChannelProfileImpl,
);
const CLIENT_CONTACT_VALUE: CubiqTS.ConcreteEncodable = CubiqTS.ENCODABLE(
  CubiqTS.ClientContactImpl,
);
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { PrimaryLookupService } from "../../impl/PrimaryLookupService";

@Injectable()
export class ClientPrimaryLookupService
  extends AutoCompleteLabelValuePrimaryLookupService<string, CubiqTS.ClientImpl>
  implements IClientPromiseLookupService
{
  //

  private rateSrc: CubiqTS.RateSourceImpl[];
  private tradingChannelProfiles: CubiqTS.TradingChannelProfileImpl[];

  constructor(
    protected base: ClientLookupService,
    protected factory: ServiceMessageBrokerFactory,
    protected cubiqWorker: CubiqWorker,
    protected branchLookup: BranchPromiseLookupService,
    protected systemConfig: SystemConfigPrimaryService,
    protected activeUserService: ActiveUserProfilePromiseService,
    protected clientModelPrivatization: ClientModelPrivatization,
    protected clientAccountVisibilityService: ClientAccountVisibilityService,
  ) {
    super(base, factory, CubiqTS.ClientImpl, cubiqWorker, "ClientImpl");

    const staticDataPromises = [
      new Promise<void>((res, rej) => {
        cubiqWorker.primeStaticData<CubiqTS.ClientImpl>(CubiqTS.ClientImpl.name).subscribe(
          (client: CubiqTS.ClientImpl[]) => {
            this.clientAccountVisibilityService.isReady().then(() => {
              let a = new Date().getTime();
              console.debug("Priming clients ...");
              this.base.prime(client);
              console.debug("Clients done in ", new Date().getTime() - a);
            });
          },
          (error: Error) => {
            console.debug(`${this.constructor.name} clients prime failed`);
            rej(error);
          },
          () => {
            this.clientAccountVisibilityService.isReady().then(() => {
              res();
            });
          },
        );
      }),

      new Promise<void>((res, rej) => {
        cubiqWorker.primeStaticData<CubiqTS.RateSourceImpl>(CubiqTS.RateSourceImpl.name).subscribe(
          (rateSource: CubiqTS.RateSourceImpl[]) => {
            this.rateSrc = rateSource;
          },
          (error: Error) => {
            console.debug(`${this.constructor.name} ${CubiqTS.RateSourceImpl.name} prime failed`);
            rej(error);
          },
          () => {
            res();
          },
        );
      }),

      new Promise<void>((res, rej) => {
        cubiqWorker
          .primeStaticData<CubiqTS.ClientContact>(CubiqTS.ClientContactImpl.name)
          .subscribe(
            (clientContacts: CubiqTS.ClientContact[]) => {
              clientContacts.forEach((contact: CubiqTS.ClientContact) =>
                this.base.mapClientContact(contact),
              );
            },
            (error: Error) => {
              console.debug(
                `${this.constructor.name} ${CubiqTS.ClientContactImpl.name} prime failed`,
              );
              rej(error);
            },
            () => {
              res();
            },
          );
      }),

      new Promise<void>((res, rej) => {
        cubiqWorker
          .primeStaticData<CubiqTS.TradingChannelProfileImpl>(
            CubiqTS.TradingChannelProfileImpl.name,
          )
          .subscribe(
            (tradingChannelProfiles: CubiqTS.TradingChannelProfileImpl[]) => {
              this.tradingChannelProfiles = tradingChannelProfiles;
              tradingChannelProfiles.forEach((profile: CubiqTS.TradingChannelProfile) =>
                this.base.mapTradingChannelProfile(profile),
              );
            },
            (error: Error) => {
              console.debug(
                `${this.constructor.name} ${CubiqTS.TradingChannelProfileImpl.name} prime failed`,
              );
              rej(error);
            },
            () => {
              res();
            },
          );
      }),
    ];

    Promise.all([
      activeUserService.getActiveUserData(),
      cubiqWorker.isAuth(`${this.constructor.name}`),
      systemConfig.isPrimed(),
      branchLookup.isPrimed(),
      this.clientModelPrivatization.ready,
    ]).then(
      (values: any[]) => {
        const user = values[0] as ActiveUser;
        const isUnrestricted = this.systemConfig.hardHasRole("ROLE_ORDER_ENTRY_UNRESTRICTED");
        const isVisibilityClientData = this.systemConfig.hardHasRole("ROLE_VISIBILITY_CLIENT_DATA");

        Promise.all(staticDataPromises).then(
          () => {
            this.addPrivatization();
            this._primed.resolve(true);
          },
          (reason) => {
            console.debug("ClientPrimaryLookupService: Failed to prime: " + reason);

            this._primed.resolve(false);
          },
        );
      },
      (reason) => {
        console.debug("ClientPrimaryLookupService: Failed to prime: " + reason);

        this._primed.resolve(false);
      },
    );

    this.broker.registerMethod("getClient", [PRIMITIVE], this.getClient.bind(this), this.VALUE);
    // this.broker.registerMethod('getBranchCodes', [], this.getBranchCodes.bind(this), PRIMITIVE);
    this.broker.registerMethod(
      "getTradingChannels",
      [],
      this.getTradingChannels.bind(this),
      TRADING_CHANNEL_PROFILE_VALUE,
    );
    this.broker.registerMethod("getRateSrc", [], this.getRateSrc.bind(this), PRIMITIVE);
    this.broker.registerMethod(
      "lookupClient",
      [PRIMITIVE, PRIMITIVE],
      this.lookupClient.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "lookupClientsForId",
      [PRIMITIVE, PRIMITIVE],
      this.lookupClientsForId.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "lookupClientsForName",
      [PRIMITIVE, PRIMITIVE],
      this.lookupClientsForName.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "getClientContacts",
      [PRIMITIVE],
      this.getClientContacts.bind(this),
      CLIENT_CONTACT_VALUE,
    );
    this.broker.registerMethod(
      "getClientContact",
      [PRIMITIVE],
      this.getClientContact.bind(this),
      CLIENT_CONTACT_VALUE,
    );
    // Active clients only
    this.broker.registerMethod(
      "getActiveClient",
      [PRIMITIVE],
      this.getActiveClient.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "getActiveClientContacts",
      [PRIMITIVE],
      this.getActiveClientContacts.bind(this),
      CLIENT_CONTACT_VALUE,
    );
    this.broker.registerMethod(
      "lookupActiveClientsForId",
      [PRIMITIVE, PRIMITIVE],
      this.lookupActiveClientsForId.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "lookupActiveClientsForName",
      [PRIMITIVE, PRIMITIVE],
      this.lookupActiveClientsForName.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "lookupAFillClientsForId",
      [PRIMITIVE, PRIMITIVE],
      this.lookupAFillClientsForId.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "lookupAFillClientsForName",
      [PRIMITIVE, PRIMITIVE],
      this.lookupAFillClientsForName.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "lookupBMarkClientsForId",
      [PRIMITIVE, PRIMITIVE],
      this.lookupBMarkClientsForId.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod(
      "lookupBMarkClientsForName",
      [PRIMITIVE, PRIMITIVE],
      this.lookupBMarkClientsForName.bind(this),
      this.VALUE,
    );
  }

  private addPrivatization(): void {
    for (let kv of this.clientModelPrivatization.getPrivate().entries()) {
      // logger.out.warn(`Adding a privatized dummy client ${kv[0]} ${kv[1]}`);
      this.base.mapPrivatizationItem(kv[1], kv[0]);
    }

    let baseBranchLookup = this.branchLookup.getBase() as BranchLookupService;

    for (let branchCd of baseBranchLookup.getVisiblePrivateBranchCodes()) {
      for (let kv of this.clientModelPrivatization.getBranchPrivate(branchCd).entries()) {
        this.base.mapPrivatizationItem(kv[1], kv[0]);
      }
    }

    for (let kv of this.clientModelPrivatization.getRestricted().entries()) {
      // logger.out.warn(`Adding a restricted dummy client ${kv[0]} ${kv[1]}`);
      this.base.mapPrivatizationItem(kv[1], kv[0]);
    }

    for (let clientContact of this.clientModelPrivatization.getClientContacts()) {
      this.base.mapClientContact(clientContact);
    }

    this.clientModelPrivatization.restricted$.subscribe((restrictedClientSegment) => {
      this.base.mapPrivatizationItem(restrictedClientSegment.value, restrictedClientSegment.key);
    });

    this.clientModelPrivatization.clientContactsRestrictedClientSegment$.subscribe(
      (restrictedClientSegment) => {
        this.base.mapClientContact(restrictedClientSegment.value);
      },
    );
  }

  protected processModelUpdateEvent(event: CubiqTS.ModelUpdateImpl): void {
    super.processModelUpdateEvent(event);

    if (
      event == Object(event) &&
      event.payLoad == Object(event.payLoad) &&
      event.payLoad instanceof ClientContactImpl
    ) {
      let clientContact: CubiqTS.ClientContactImpl = event.payLoad as CubiqTS.ClientContactImpl;

      if (event.eventType == CubiqTS.ModelUpdateType.DELETE) {
        this.base.removeClientContact(clientContact.id.toString());
      } else {
        this.base.mapClientContact(clientContact);
      }

      this.fireModelUpdateEvent(event);
    }
  }

  getClient(id: string, type?: ClientAccountVisibilityType | null): Promise<CubiqTS.ClientImpl> {
    return new Promise<CubiqTS.ClientImpl>((res, rej) => {
      res(this.base.getClient(id, type));
    });
  }

  lookupClient(namePrefix: string, maxResults: number): Promise<Array<CubiqTS.ClientImpl>> {
    return new Promise<Array<CubiqTS.ClientImpl>>((res, rej) => {
      res(this.base.lookupClient(namePrefix, maxResults));
    });
  }

  lookupClientsForId(namePrefix: string, maxResults: number): Promise<Array<CubiqTS.ClientImpl>> {
    return new Promise<Array<CubiqTS.ClientImpl>>((res, rej) => {
      res(this.base.lookupClientsForId(namePrefix, maxResults));
    });
  }

  lookupClientsForName(namePrefix: string, maxResults: number): Promise<Array<CubiqTS.ClientImpl>> {
    return new Promise<Array<CubiqTS.ClientImpl>>((res, rej) => {
      res(this.base.lookupClientsForName(namePrefix, maxResults));
    });
  }

  getTradingChannels(): Promise<CubiqTS.TradingChannelProfileImpl[]> {
    return new Promise<Array<CubiqTS.TradingChannelProfileImpl>>((res, rej) => {
      res(this.tradingChannelProfiles);
    });
  }

  getRateSrc(): Promise<CubiqTS.RateSourceImpl[]> {
    return new Promise<Array<CubiqTS.RateSourceImpl>>((res, rej) => {
      res(this.rateSrc);
    });
  }

  getClientContacts(id: string): Promise<CubiqTS.ClientContact[]> {
    return new Promise<Array<CubiqTS.ClientContact>>((res, rej) => {
      res(this.base.getClientContacts(id));
    });
  }

  getClientContact(id: string): Promise<CubiqTS.ClientContact> {
    return new Promise<CubiqTS.ClientContact>((res, rej) => {
      res(this.base.getClientContact(id));
    });
  }

  getActiveClientContacts(id: string): Promise<CubiqTS.ClientContact[]> {
    return new Promise<Array<CubiqTS.ClientContact>>((res, rej) => {
      res(this.base.getClientContacts(id, true));
    });
  }

  /*  ACTIVE CLIENTS ONLY */
  getActiveClient(
    id: string,
    type?: ClientAccountVisibilityType | null,
  ): Promise<CubiqTS.ClientImpl> {
    return new Promise<CubiqTS.ClientImpl>((res, rej) => {
      res(this.base.getActiveClient(id, type));
    });
  }

  lookupActiveClientsForId(
    namePrefix: string,
    maxResults: number,
    onlyFullVisibility: boolean = false,
  ): Promise<Array<CubiqTS.ClientImpl>> {
    return new Promise<Array<CubiqTS.ClientImpl>>((res, rej) => {
      //let dataset: Array<CubiqTS.ClientImpl> = this.base.lookupClientsForId(namePrefix, maxResults, true);
      res(
        onlyFullVisibility
          ? this.filterFullyVisibleClients(
              this.base.lookupClientsForId(namePrefix, 0, true),
              maxResults,
            )
          : this.base.lookupClientsForId(namePrefix, maxResults, true),
      );
    });
  }

  lookupActiveClientsForName(
    namePrefix: string,
    maxResults: number,
    onlyFullVisibility: boolean = false,
  ): Promise<Array<CubiqTS.ClientImpl>> {
    return new Promise<Array<CubiqTS.ClientImpl>>((res, rej) => {
      res(
        onlyFullVisibility
          ? this.filterFullyVisibleClients(
              this.base.lookupClientsForName(namePrefix, 0, true),
              maxResults,
            )
          : this.base.lookupClientsForName(namePrefix, maxResults, true),
      );
    });
  }

  lookupAFillClientsForId(
    namePrefix: string,
    maxResults: number,
    onlyFullVisibility: boolean = false,
  ): Promise<Array<CubiqTS.ClientImpl>> {
    return new Promise<Array<CubiqTS.ClientImpl>>((res, rej) => {
      res(
        onlyFullVisibility
          ? this.filterFullyVisibleClients(
              this.base.lookupClientsForId(namePrefix, 0, true, true),
              maxResults,
            )
          : this.base.lookupClientsForId(namePrefix, maxResults, true, true),
      );
    });
  }

  lookupAFillClientsForName(
    namePrefix: string,
    maxResults: number,
    onlyFullVisibility: boolean = false,
  ): Promise<Array<CubiqTS.ClientImpl>> {
    return new Promise<Array<CubiqTS.ClientImpl>>((res, rej) => {
      res(
        onlyFullVisibility
          ? this.filterFullyVisibleClients(
              this.base.lookupClientsForName(namePrefix, 0, true, true),
              maxResults,
            )
          : this.base.lookupClientsForName(namePrefix, maxResults, true, true),
      );
    });
  }

  lookupBMarkClientsForId(
    id: string,
    maxResults: number,
    onlyFullVisibility: boolean = false,
  ): Promise<CubiqTS.ClientImpl[]> {
    return new Promise<Array<CubiqTS.ClientImpl>>((res, rej) => {
      res(
        onlyFullVisibility
          ? this.filterFullyVisibleClients(
              this.base.lookupClientsForId(id, 0, true, false, true),
              maxResults,
            )
          : this.base.lookupClientsForId(id, maxResults, true, false, true),
      );
    });
  }
  lookupBMarkClientsForName(
    namePrefix: string,
    maxResults: number,
    onlyFullVisibility: boolean = false,
  ): Promise<CubiqTS.ClientImpl[]> {
    return new Promise<Array<CubiqTS.ClientImpl>>((res, rej) => {
      res(
        onlyFullVisibility
          ? this.filterFullyVisibleClients(
              this.base.lookupClientsForName(namePrefix, 0, true, false, true),
              maxResults,
            )
          : this.base.lookupClientsForName(namePrefix, maxResults, true, false, true),
      );
    });
  }

  /// OMS-31021
  // Filters the dataset of clients, based on users visibility, only fully visible clients are returend
  private filterFullyVisibleClients(
    dataset: Array<CubiqTS.ClientImpl>,
    maxResults: number,
  ): Array<CubiqTS.ClientImpl> {
    if (Array.isArray(dataset) && dataset.length > 0) {
      let result: Array<CubiqTS.ClientImpl> = new Array<CubiqTS.ClientImpl>();
      for (let client of dataset) {
        if (
          client instanceof CubiqTS.ClientImpl &&
          this.clientAccountVisibilityService._getVisibilityType(client) ===
            ClientAccountVisibilityType.Full
        ) {
          result.push(client);
        }

        if (maxResults > 0 && result.length >= maxResults) {
          break;
        }
      }
      return result;
    } else {
      return [];
    }
  }
}
